const escapeHtml = (value: unknown): string =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * For user text BAKED into a page that `resolveSystemTokens` scans afterwards
 * (the preview head): braces are encoded too, so a story name spelling
 * `{{{system.pageTitle}}}` cannot turn into a raw-insert token.
 */
export const escapeBakedHtml = (value: unknown): string =>
  escapeHtml(value).replace(/\{/g, "&#123;").replace(/\}/g, "&#125;");

/**
 * Fill the page template's `{{system.x.y}}` tokens for HTML that never goes
 * through the server — the preview page is rendered and uploaded straight
 * from the browser, so its canonical URL, Open Graph image, PDF link and gate
 * config stayed as raw `{{system.…}}` text. Same rules as dotell-backend
 * `services/handlebars.js#resolveSystemTokens`: HTML-escaped values (every
 * sink is text or a quoted attribute), `{{{…}}}` raw, an unknown path blank.
 */
export const resolveSystemTokens = (
  html: string,
  system: Record<string, unknown>,
): string =>
  html.replace(
    /\{\{(\{?)\s*system\.([\w.]+)\s*\}\}(\}?)/g,
    (_match, open: string, path: string, close: string) => {
      const value = path
        .split(".")
        .reduce<unknown>(
          (acc, key) =>
            acc != null &&
            typeof acc === "object" &&
            Object.prototype.hasOwnProperty.call(acc, key)
              ? (acc as Record<string, unknown>)[key]
              : undefined,
          system,
        );
      if (open && close) return value == null ? "" : String(value);
      return open + (value == null ? "" : escapeHtml(value)) + close;
    },
  );
